import useTracksStore, {
  TrackType,
  VOICE_TRACK_ID,
} from '@/shared/store/tracks.store.tsx'
import useMediaStore from '@/shared/store/media.store.tsx'
import type {
  AudioFormClip,
  StsScriptAsset,
  TtsScriptAsset,
  VoiceAsset,
} from '@/features/audio-form/data/dto/audio-form-input.ts'
import { VoiceService } from '@/features/voice/data/voice.service.ts'
import { mapVoiceDtoToVoice } from '@/features/voice/domain/mappers/map-voice-dto-to-voice.ts'
import type { Voice } from '@/features/voice/domain/models/voice.ts'
import { mapAudioFormClipToClipPositions } from './map-audio-form-clip-to-clip-positions.ts'
import { useScriptStore } from '@/shared/store/script.store.ts'
import { createScript } from '@/features/script/domain/use-cases/create-script.ts'
import type { Result } from '@siroc/types'
import type { Clip } from '@/features/clips/domain/models/clip.ts'
import { useSpeechStore } from '@/shared/store/speech.store.ts'
import type { SpeechData } from '@/features/session/domain/models/session.ts'
import { getAudioBufferFromAudioUrl } from '@/utils/get-audio-buffer-from-audio-url.ts'
import { MediaFileService } from '@/features/media-file/data/media-file.service.ts'
import { createTTSSpeech } from '@/features/speech/domain/use-cases/create-tts-speech.ts'
import { createSTSSpeech } from '@/features/speech/domain/use-cases/create-sts-speech.ts'

const resolveTtsSpeech = async (
  tts: TtsScriptAsset,
  scriptId: string,
  voice: Voice,
  speed: number,
  expressive: boolean,
  clipAssetRef: string
): Promise<{
  speechId: string
  speechUrl: string
  audioBuffer: AudioBuffer
}> => {
  const { addMedia } = useMediaStore.getState()
  const { storeSpeech } = useSpeechStore.getState()

  if (tts.uri) {
    const audioBuffer = await getAudioBufferFromAudioUrl(tts.uri)
    addMedia(clipAssetRef, audioBuffer)
    const speechData: SpeechData = {
      type: 'tts',
      speechId: clipAssetRef,
      previewUrl: tts.uri,
      scriptId,
      voice,
      expressive,
      speed,
    }
    if (tts.anchor) {
      speechData.anchor = tts.anchor
    }
    storeSpeech(clipAssetRef, speechData)
    return { speechId: clipAssetRef, speechUrl: tts.uri, audioBuffer }
  }

  const createdSpeech = await createTTSSpeech(
    scriptId,
    voice,
    speed,
    expressive
  )
  const audioBuffer = await getAudioBufferFromAudioUrl(createdSpeech.speechUrl)
  addMedia(createdSpeech.speechId, audioBuffer)
  storeSpeech(createdSpeech.speechId, {
    type: 'tts',
    speechId: createdSpeech.speechId,
    previewUrl: createdSpeech.speechUrl,
    scriptId,
    voice,
    expressive,
    speed,
  })
  return {
    speechId: createdSpeech.speechId,
    speechUrl: createdSpeech.speechUrl,
    audioBuffer,
  }
}

export const mapTtsSpeechLayerToClips = async (
  tts: TtsScriptAsset,
  voiceAsset: VoiceAsset,
  clip: AudioFormClip
): Promise<void> => {
  const clipId = crypto.randomUUID()

  const voiceDto = await VoiceService.getVoiceByAlias(voiceAsset.voiceAlias)
  if (!voiceDto) return
  const scriptIdResult = getTtsScriptId(tts)
  if (!scriptIdResult.ok) {
    // todo: show toast
    return
  }
  const voice = mapVoiceDtoToVoice(voiceDto)
  const speed = voiceAsset.speed ?? 1
  const expressive = voiceAsset.voicePreset === 'expressive'

  // Load clip into tracks store
  const { addClip } = useTracksStore.getState()

  const { speechId, speechUrl, audioBuffer } = await resolveTtsSpeech(
    tts,
    scriptIdResult.data,
    voice,
    speed,
    expressive,
    clip.assetRef
  )

  const voiceClip: Clip = {
    id: clipId,
    type: TrackType.Voices,
    trackId: VOICE_TRACK_ID,
    name: voiceAsset.voiceAlias,
    mediaId: speechId,
    speechType: 'tts',
    src: speechUrl,
    ...mapAudioFormClipToClipPositions(clip, audioBuffer.duration),
  }

  addClip(VOICE_TRACK_ID, voiceClip)
}

interface ResolvedSpeech {
  speechId: string
  speechUrl: string
  audioBuffer: AudioBuffer
  scriptId: string
}

const resolveStsSpeech = async (
  sts: StsScriptAsset,
  voice: Voice,
  speed: number,
  expressive: boolean,
  clipAssetRef: string
): Promise<ResolvedSpeech | undefined> => {
  const { addMedia } = useMediaStore.getState()
  const { storeSpeech } = useSpeechStore.getState()

  if (sts.uri) {
    const audioBuffer = await getAudioBufferFromAudioUrl(sts.uri)
    addMedia(clipAssetRef, audioBuffer)
    const speechData: SpeechData = {
      type: 'sts',
      speechId: clipAssetRef,
      fileId: sts.mediaRef,
      previewUrl: sts.uri,
      scriptId: '',
      voice,
      expressive,
      speed,
    }
    storeSpeech(clipAssetRef, speechData)
    return {
      speechId: clipAssetRef,
      speechUrl: sts.uri,
      audioBuffer,
      scriptId: '',
    }
  }

  const scriptIdResult = await getStsScriptId(sts)
  if (!scriptIdResult.ok) return undefined

  const result = await createSTSSpeech(
    scriptIdResult.data,
    sts.mediaRef,
    voice,
    expressive
  )
  const audioBuffer = await getAudioBufferFromAudioUrl(result.speechUrl)
  addMedia(result.speechId, audioBuffer)
  const speechData: SpeechData = {
    type: 'sts',
    speechId: result.speechId,
    fileId: sts.mediaRef,
    previewUrl: result.speechUrl,
    scriptId: scriptIdResult.data,
    voice,
    expressive,
    speed,
  }
  storeSpeech(result.speechId, speechData)
  return {
    speechId: result.speechId,
    speechUrl: result.speechUrl,
    audioBuffer,
    scriptId: scriptIdResult.data,
  }
}

export const mapStsSpeechLayerToClips = async (
  sts: StsScriptAsset,
  voiceAsset: VoiceAsset,
  clip: AudioFormClip
): Promise<void> => {
  const clipId = crypto.randomUUID()

  const voiceDto = await VoiceService.getVoiceByAlias(voiceAsset.voiceAlias)
  if (!voiceDto) return
  const voice = mapVoiceDtoToVoice(voiceDto)
  const expressive = voiceAsset.voicePreset === 'expressive'

  // Load clip into tracks store
  const { addClip } = useTracksStore.getState()

  const resolvedSpeech = await resolveStsSpeech(
    sts,
    voice,
    voiceAsset.speed ?? 1,
    expressive,
    clip.assetRef
  )

  if (!resolvedSpeech) return

  const { speechId, speechUrl, audioBuffer } = resolvedSpeech

  const voiceClip: Clip = {
    id: clipId,
    type: TrackType.Voices,
    trackId: VOICE_TRACK_ID,
    name: voiceAsset.voiceAlias,
    mediaId: speechId,
    speechType: 'sts',
    src: speechUrl,
    ...mapAudioFormClipToClipPositions(clip, audioBuffer.duration),
  }

  addClip(VOICE_TRACK_ID, voiceClip)
}

const getTtsScriptId = (tts: TtsScriptAsset): Result<string> => {
  const { scripts } = useScriptStore.getState()

  const script = scripts.find((s) => s.text === tts.text)
  if (script) {
    return { ok: true, data: script.id }
  }

  const scriptResult = createScript(tts.text, true)
  if (!scriptResult.ok) {
    return { ok: false, error: scriptResult.error }
  }
  return { ok: true, data: scriptResult.data.id }
}

const getStsScriptId = async (
  _sts: StsScriptAsset
): Promise<Result<string>> => {
  const pipelineId = await MediaFileService.transcribe(_sts.mediaRef)
  const transcription = await MediaFileService.pipeline(pipelineId)
  const scriptResult = createScript(transcription, true)
  if (!scriptResult.ok) {
    return { ok: false, error: scriptResult.error }
  }
  return { ok: true, data: scriptResult.data.id }
}
